import React, { useEffect, useState } from "react";
import { api, formatError } from "../../lib/api";
import { toast } from "sonner";
import { Image as ImageIcon, Upload, RotateCcw } from "lucide-react";

const SLOTS = [
  { key: "hero", label: "Hero image (THE MOVEMENT)", hint: "Top of the Welcome page. 4:5 portrait works best." },
  { key: "basavanna", label: "Basavanna section (Who Was Basavanna)", hint: "Heritage / 12th-century visual. 4:5 portrait." },
  { key: "early", label: "Early Learning section", hint: "Children listening / reading. 4:5 portrait." },
];

export default function AdminWelcomeImages() {
  const [imgs, setImgs] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const FILES = `${process.env.REACT_APP_BACKEND_URL}/api/files/`;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/public/welcome-images");
      setImgs(data || {});
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const upload = async (slot, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be 5MB or smaller"); return; }
    setUploading(slot);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/welcome-images/${slot}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Image updated");
      await load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setUploading(null); }
  };

  const reset = async (slot) => {
    if (!confirm("Reset to default image?")) return;
    try {
      await api.delete(`/admin/welcome-images/${slot}`);
      toast.success("Reset to default");
      await load();
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="admin-welcome-tab">
      <div className="bb-card p-6">
        <div className="bb-overline text-[var(--bb-muted)] mb-2 flex items-center gap-2"><ImageIcon size={14} /> Welcome page images</div>
        <h3 className="font-serif text-2xl text-[var(--bb-forest)]">Welcome page imagery</h3>
        <p className="text-sm text-[var(--bb-muted)] mt-2">
          Upload custom images for the three key sections of <a className="bb-link" href="/welcome" target="_blank" rel="noreferrer">/welcome</a>.
          JPG, PNG or WebP · max 5 MB each. Leaving a slot empty shows the default image.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-5">
        {SLOTS.map((s) => {
          const url = imgs[s.key] ? FILES + imgs[s.key] : null;
          return (
            <div key={s.key} className="bb-card p-5 flex flex-col" data-testid={`welcome-slot-${s.key}`}>
              <div className="aspect-[4/5] rounded-md overflow-hidden bg-[var(--bb-sand)] mb-4 ring-1 ring-[var(--bb-border)]">
                {loading ? (
                  <div className="w-full h-full grid place-items-center text-xs text-[var(--bb-muted)]">Loading…</div>
                ) : url ? (
                  <img src={url} alt={s.label} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full grid place-items-center text-center p-4">
                    <div>
                      <ImageIcon size={28} className="text-[var(--bb-muted)] mx-auto mb-2" />
                      <div className="text-xs text-[var(--bb-muted)]">Default image in use</div>
                    </div>
                  </div>
                )}
              </div>
              <div className="font-serif text-base text-[var(--bb-forest)] leading-snug">{s.label}</div>
              <div className="text-[11px] text-[var(--bb-muted)] mt-1 mb-4">{s.hint}</div>

              <label className={`bb-btn-primary text-sm inline-flex items-center justify-center gap-2 cursor-pointer ${uploading === s.key ? "opacity-60 pointer-events-none" : ""}`} data-testid={`welcome-upload-${s.key}`}>
                <Upload size={13} /> {uploading === s.key ? "Uploading…" : (url ? "Replace image" : "Upload image")}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => upload(s.key, e.target.files?.[0])}
                />
              </label>
              {url && (
                <button
                  onClick={() => reset(s.key)}
                  className="mt-2 text-xs text-[var(--bb-muted)] hover:text-[var(--bb-error)] inline-flex items-center justify-center gap-1"
                  data-testid={`welcome-reset-${s.key}`}
                >
                  <RotateCcw size={11} /> Reset to default
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
